import type { DashboardContract, Locale } from "@chams-pay/contracts";
import { formatWholeMad } from "@chams-pay/contracts";

/** The school operates on Casablanca civil dates, never the device time zone. */
export function casablancaDate(): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Africa/Casablanca",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${values.year}-${values.month}-${values.day}`;
}

export function casablancaMonth(): string {
  return casablancaDate().slice(0, 7);
}

export function monthBounds(month: string): { from: string; to: string } {
  const [year, value] = month.split("-").map(Number) as [number, number];
  const last = new Date(Date.UTC(year, value, 0)).getUTCDate();
  return { from: `${month}-01`, to: `${month}-${String(last).padStart(2, "0")}` };
}

export type DashboardPeriodPreset = "week" | "month" | "academic-year";

function shiftCivilDate(value: string, days: number): string {
  const [year, month, day] = value.split("-").map(Number) as [number, number, number];
  return new Date(Date.UTC(year, month - 1, day + days)).toISOString().slice(0, 10);
}

function shiftCivilMonth(value: string, months: number): string {
  const [year, month] = value.split("-").map(Number) as [number, number];
  return new Date(Date.UTC(year, month - 1 + months, 1)).toISOString().slice(0, 7);
}

/** Completes the selected dashboard range with explicit zero-value activity buckets. */
export function completeDashboardActivity(
  activity: DashboardContract["activity"],
  period: DashboardContract["period"],
): DashboardContract["activity"] {
  const keyOf = (value: string) =>
    activity.granularity === "day" ? value.slice(0, 10) : value.slice(0, 7);
  const existing = new Map(activity.points.map((point) => [keyOf(point.period), point]));
  const periods: string[] = [];

  if (activity.granularity === "day") {
    for (let value = period.from; value <= period.to; value = shiftCivilDate(value, 1)) {
      periods.push(value);
    }
  } else {
    const finalMonth = period.to.slice(0, 7);
    for (
      let value = period.from.slice(0, 7);
      value <= finalMonth;
      value = shiftCivilMonth(value, 1)
    ) {
      periods.push(`${value}-01`);
    }
  }

  return {
    granularity: activity.granularity,
    points: periods.map(
      (value) =>
        existing.get(keyOf(value)) ?? {
          period: value,
          cashMad: "0",
          chequeMad: "0",
          transferMad: "0",
          netMad: "0",
        },
    ),
  };
}

export function dashboardPeriodPresetBounds(
  preset: DashboardPeriodPreset,
  academicYear: string | undefined,
  today = casablancaDate(),
): { from: string; to: string } {
  if (preset === "week") return { from: shiftCivilDate(today, -6), to: today };
  if (preset === "month") return { ...monthBounds(today.slice(0, 7)), to: today };
  const match = /^(\d{4})-(\d{4})$/.exec(academicYear ?? "");
  if (!match) return { ...monthBounds(today.slice(0, 7)), to: today };
  const start = Number(match[1]);
  const end = `${start + 1}-06-30`;
  return { from: `${start}-07-01`, to: today < end ? today : end };
}

/** Payable periods are either `YYYY-MM` months or the enrollment charge. */
export function periodLabel(period: string, locale: Locale): string {
  if (!/^\d{4}-\d{2}$/.test(period)) return locale === "ar" ? "التسجيل" : "Inscription";
  return new Intl.DateTimeFormat(locale === "ar" ? "ar-MA-u-nu-latn" : "fr-MA-u-nu-latn", {
    month: "long",
  }).format(new Date(`${period}-01T12:00:00Z`));
}

export function formatStamp(value: string, locale: Locale): string {
  return new Intl.DateTimeFormat(locale === "ar" ? "ar-MA-u-nu-latn" : "fr-MA-u-nu-latn", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(new Date(value));
}

export function money(value: string, locale: Locale): string {
  return formatWholeMad(BigInt(value), locale);
}

export type StudentServiceCode = "tuition" | "transport" | "accompaniment";

const serviceMatchers: readonly {
  readonly code: StudentServiceCode;
  readonly matches: readonly string[];
}[] = [
  { code: "tuition", matches: ["الدراسة", "تمدرس", "scolarité"] },
  { code: "transport", matches: ["النقل", "transport"] },
  { code: "accompaniment", matches: ["المواكبة", "المرافقة", "accompagnement"] },
];

/** Derives the enrolled services from the bilingual service summary strings. */
export function studentServices(
  summaryAr: string,
  summaryFr: string,
): readonly StudentServiceCode[] {
  const source = `${summaryAr} ${summaryFr}`.toLocaleLowerCase("fr");
  return serviceMatchers
    .filter((service) => service.matches.some((match) => source.includes(match)))
    .map((service) => service.code);
}

export function initialOf(name: string): string {
  return name.trim().slice(0, 1).toLocaleUpperCase("fr");
}
